import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import GenericDataGrid from '../../../components/genericTable';
import NotificationSnackbar from '../../../components/notificationSnackbar';
import { createManagementTableSx } from '../../../styles/tableStyles';
import {
    managementCreateButtonSx,
    managementHeaderActionSx,
    managementHeaderContentSx,
    managementHeaderSx,
    managementPageSubtitleSx,
} from '../../../styles/managementStyles';
import { buttonContentSx } from '../../../styles/dialogStyles';
import { compactTableActionButtonSx, tableDeleteButtonSx, tableViewButtonSx } from '../../../styles/actionStyles';
import { MspPriceModal } from './marketReferenceModals';
import { deleteMspPrice, getMspPrices, mspSeasonLabel, sortMspPrices } from './marketReference.service';

const textCellSx = { fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' };

const cardSx = { p: 3, borderRadius: '16px', border: '1px solid #DFE5F6', backgroundColor: '#FFFFFF' };

function formatDate(value) {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Minimum Support Prices the market signals compare mandi prices and sale rates against. A crop
 * with no row simply gets no MSP comparison; the price-volatility signal still runs.
 */
function MspPrices() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    const load = useCallback(async () => {
        setLoading(true);
        const result = await getMspPrices();
        setRows(result.success ? result.data : []);
        if (!result.success) notify(result.error.message, 'error');
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const sorted = useMemo(() => sortMspPrices(rows), [rows]);

    const handleDelete = async (id) => {
        const result = await deleteMspPrice(id);
        if (!result.success) {
            notify(result.error.message, 'error');
            return;
        }
        await load();
        notify('MSP price removed.');
    };

    const openAdd = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const columns = [
        {
            field: 'cropName',
            headerName: 'Crop',
            flex: 1.1,
            minWidth: 160,
            sortable: false,
            renderCell: (params) => <Typography sx={{ ...textCellSx, fontWeight: 600 }}>{params.value}</Typography>,
        },
        {
            field: 'countryName',
            headerName: 'Country',
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{params.value || '-'}</Typography>,
        },
        {
            field: 'seasonCode',
            headerName: 'Season',
            flex: 0.7,
            minWidth: 110,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{mspSeasonLabel(params.value)}</Typography>,
        },
        {
            field: 'marketingYear',
            headerName: 'Year',
            flex: 0.6,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{params.value || '-'}</Typography>,
        },
        {
            field: 'mspValue',
            headerName: 'MSP / quintal',
            flex: 0.8,
            minWidth: 130,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ ...textCellSx, fontWeight: 600 }}>
                    ₹{Number(params.value).toLocaleString('en-IN')}
                </Typography>
            ),
        },
        {
            field: 'notifiedAt',
            headerName: 'Notified',
            flex: 0.8,
            minWidth: 130,
            sortable: false,
            renderCell: (params) => <Typography sx={textCellSx}>{formatDate(params.value)}</Typography>,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            minWidth: 200,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                    <Button
                        sx={{ ...tableViewButtonSx, ...compactTableActionButtonSx }}
                        onClick={() => {
                            setEditing(params.row);
                            setModalOpen(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button
                        sx={{ ...tableDeleteButtonSx, ...compactTableActionButtonSx }}
                        onClick={() => handleDelete(params.row.id)}
                    >
                        Delete
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <>
            <Box sx={managementHeaderSx}>
                <Box sx={managementHeaderContentSx}>
                    <Typography sx={managementPageSubtitleSx}>
                        Minimum Support Prices, per country. eK Score compares mandi prices and farmers' sale rates
                        against them. Only farmers in the price's country are compared, and the newest marketing year
                        wins.
                    </Typography>
                </Box>
                <Box sx={managementHeaderActionSx}>
                    <Button
                        variant="contained"
                        onClick={openAdd}
                        sx={{ ...managementCreateButtonSx, width: { xs: '100%', md: 'auto' }, minWidth: { xs: '100%', md: 204 } }}
                    >
                        <Box component="span" sx={buttonContentSx}>
                            <Icon icon="tabler:plus" width="18" height="18" />
                            <span>ADD PRICE</span>
                        </Box>
                    </Button>
                </Box>
            </Box>

            {sorted.length === 0 && !loading ? (
                <Paper elevation={0} sx={{ ...cardSx, textAlign: 'center', py: 6 }}>
                    <Icon icon="tabler:currency-rupee" width="40" height="40" color="#94A3B8" />
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#1C1B1F', mt: 2, mb: 1 }}>
                        No MSP prices yet
                    </Typography>
                    <Typography sx={{ color: '#625B71', maxWidth: 620, mx: 'auto' }}>
                        Without them the price-gap signal and the "sale below floor" warning are skipped. Scores stay
                        honest; they just carry less market evidence.
                    </Typography>
                </Paper>
            ) : (
                <GenericDataGrid
                    rows={sorted.slice(page * size, page * size + size)}
                    columns={columns}
                    rowCount={sorted.length}
                    page={page}
                    pageSize={size}
                    loading={loading}
                    dataGridSx={createManagementTableSx()}
                    tableMinWidth={1080}
                    rowHeight={60}
                    onPaginationChange={(nextPage, nextSize) => {
                        setPage(nextPage);
                        setSize(nextSize);
                    }}
                />
            )}

            <MspPriceModal
                open={modalOpen}
                price={editing}
                existing={rows}
                onClose={() => setModalOpen(false)}
                onSaved={async () => {
                    setModalOpen(false);
                    await load();
                    notify('MSP price saved.');
                }}
            />

            <NotificationSnackbar
                open={snackbar.open}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </>
    );
}

export default MspPrices;
